/**
 * Shrinking an image before it is uploaded (docs/EMAIL_IMAGES.md).
 *
 * Done here rather than on the server so that no image-processing dependency enters
 * `requirements.txt` — a `<canvas>` does the work the browser already has the code for. It is
 * also where it belongs: a 6 MB phone photo should not cross the wire at all, let alone become a
 * 6 MB attachment on four hundred mails.
 */

/** Must match MAX_IMAGE_BYTES in app/services/email_image_service.py. */
export const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

/** Wider than any mail client renders, so nothing is lost that would have been seen. */
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.85;

export interface DownscaledImage {
  file: File;
  width: number;
  height: number;
}

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Not a readable image'));
    };
    image.src = url;
  });
}

/**
 * *file*, resized to fit `MAX_EDGE` and re-encoded, with the dimensions the markup should carry.
 *
 * Returned unchanged when it is already small enough, and for GIFs whatever their size: redrawing
 * one through a canvas keeps the first frame only, so an animation would silently lose its
 * animation. An oversized GIF is rejected by the server instead, which is the honest outcome.
 */
export async function downscaleImageFile(file: File): Promise<DownscaledImage> {
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Use a PNG, JPEG, GIF or WebP image');
  }

  if (file.type === 'image/gif') {
    const { width, height } = await loadImage(file).catch(() => ({ width: 0, height: 0 }));
    return { file, width, height };
  }

  const image = await loadImage(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));

  // Already small enough on both counts — re-encoding would only lose quality
  if (scale === 1 && file.size <= MAX_IMAGE_BYTES) {
    return { file, width, height };
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return { file, width, height };
  context.drawImage(image, 0, 0, width, height);

  // PNG is kept as PNG: re-encoding to JPEG would flatten transparency onto black
  const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, type, type === 'image/jpeg' ? JPEG_QUALITY : undefined)
  );
  if (!blob) return { file, width, height };

  // If the re-encode came out bigger (small PNGs sometimes do), keep the original
  if (blob.size >= file.size && scale === 1) return { file, width, height };

  const extension = type === 'image/png' ? 'png' : 'jpg';
  const name = file.name.replace(/\.[^.]+$/, '') || 'image';
  return { file: new File([blob], `${name}.${extension}`, { type }), width, height };
}

/** The image file in a paste or drop, if there is exactly one worth taking. */
export function imageFileFrom(items: DataTransferItemList | FileList | null): File | null {
  if (!items) return null;
  const files = 'length' in items ? Array.from(items as ArrayLike<DataTransferItem | File>) : [];
  for (const entry of files) {
    const file = entry instanceof File ? entry : entry.kind === 'file' ? entry.getAsFile() : null;
    if (file && ALLOWED_TYPES.includes(file.type)) return file;
  }
  return null;
}
